/**
 * Five-field cron (`minute hour day-of-month month day-of-week`), evaluated against a wall clock in an IANA
 * timezone. Small on purpose: schedules are the only caller, and a dependency that parses every cron dialect
 * would outweigh the handful of shapes a task cadence needs.
 */

export type CronField = {
    /** Values this field accepts. */
    allowed: Set<number>
    /** `*` (or an unrestricted range) — the day-of-month / day-of-week rule below turns on this distinction. */
    wildcard: boolean
}

export type ParsedCron = {
    minute: CronField
    hour: CronField
    dayOfMonth: CronField
    month: CronField
    dayOfWeek: CronField
}

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const FIELDS = [
    { name: 'minute', min: 0, max: 59, names: [] as string[] },
    { name: 'hour', min: 0, max: 23, names: [] as string[] },
    { name: 'day of month', min: 1, max: 31, names: [] as string[] },
    { name: 'month', min: 1, max: 12, names: MONTH_NAMES },
    // 7 is Sunday too, the way every cron accepts it; `parseTerm` folds it onto 0.
    { name: 'day of week', min: 0, max: 7, names: DAY_NAMES },
] as const

export class CronError extends Error {}

/** Parse a five-field expression, or throw `CronError` naming the field at fault. */
export function parseCron(expression: string): ParsedCron {
    const parts = expression.trim().split(/\s+/)
    if (parts.length !== FIELDS.length) {
        throw new CronError(
            `A cron expression has five fields (minute hour day-of-month month day-of-week); got ${parts.length}.`,
        )
    }
    const [minute, hour, dayOfMonth, month, dayOfWeek] = FIELDS.map((spec, index) =>
        parseField(parts[index] ?? '', spec),
    ) as [CronField, CronField, CronField, CronField, CronField]
    return { minute, hour, dayOfMonth, month, dayOfWeek }
}

/** The parse error as a sentence for a form, or null when `expression` is valid. */
export function cronError(expression: string): string | null {
    try {
        parseCron(expression)
        return null
    } catch (caught) {
        return caught instanceof Error ? caught.message : 'That is not a cron expression.'
    }
}

export function isValidTimezone(timezone: string): boolean {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone })
        return true
    } catch {
        return false
    }
}

export type WallClock = {
    year: number
    /** 1-12. */
    month: number
    /** 1-31. */
    day: number
    /** 0-23. */
    hour: number
    minute: number
    /** 0 is Sunday. */
    weekday: number
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timezone: string): Intl.DateTimeFormat {
    const cached = formatters.get(timezone)
    if (cached) {
        return cached
    }
    const made = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        weekday: 'short',
    })
    formatters.set(timezone, made)
    return made
}

/** The wall clock an instant shows in `timezone`. DST is whatever the zone database says it is. */
export function wallClock(instant: Date, timezone: string): WallClock {
    const parts = formatterFor(timezone).formatToParts(instant)
    const read = (type: Intl.DateTimeFormatPartTypes): string => parts.find(part => part.type === type)?.value ?? '0'
    return {
        year: Number(read('year')),
        month: Number(read('month')),
        day: Number(read('day')),
        hour: Number(read('hour')),
        minute: Number(read('minute')),
        weekday: Math.max(0, DAY_NAMES.indexOf(read('weekday').slice(0, 3).toLowerCase())),
    }
}

/**
 * Whether the minute `instant` falls in is a firing minute. Day-of-month and day-of-week follow the rule every
 * cron uses: restrict both and a day matching *either* fires; leave one `*` and only the other decides.
 */
export function cronMatches(instant: Date, cron: ParsedCron, timezone: string): boolean {
    const clock = wallClock(instant, timezone)
    return cronMatchesClock(clock, cron)
}

function cronMatchesClock(clock: WallClock, cron: ParsedCron): boolean {
    return cron.minute.allowed.has(clock.minute) && cron.hour.allowed.has(clock.hour) && dayMatches(clock, cron)
}

function dayMatches(clock: WallClock, cron: ParsedCron): boolean {
    if (!cron.month.allowed.has(clock.month)) {
        return false
    }
    const byDate = cron.dayOfMonth.allowed.has(clock.day)
    const byWeekday = cron.dayOfWeek.allowed.has(clock.weekday)
    if (cron.dayOfMonth.wildcard) {
        return byWeekday
    }
    if (cron.dayOfWeek.wildcard) {
        return byDate
    }
    return byDate || byWeekday
}

const MINUTE_MS = 60_000
const SEARCH_DAYS = 366

/**
 * The first firing minute strictly after `after`, or null when nothing fires within a year (`0 0 30 2 *`).
 * Walks real instants and asks the zone what the wall clock reads, so an hour DST skips simply never fires and
 * an hour it repeats fires twice — the same answer the underlying zone data gives any other cron.
 */
export function nextCronFire(cron: ParsedCron, timezone: string, after: Date): Date | null {
    const limit = after.getTime() + SEARCH_DAYS * 24 * 60 * MINUTE_MS
    let cursor = Math.floor(after.getTime() / MINUTE_MS) * MINUTE_MS + MINUTE_MS
    while (cursor <= limit) {
        const clock = wallClock(new Date(cursor), timezone)
        if (!dayMatches(clock, cron)) {
            // Skip the rest of a day that cannot fire, staying an hour short of local midnight so a DST jump
            // cannot carry the cursor over the start of the next day.
            const toMidnight = 24 * 60 - (clock.hour * 60 + clock.minute)
            cursor += Math.max(1, toMidnight - 60) * MINUTE_MS
            continue
        }
        if (cron.hour.allowed.has(clock.hour) && cron.minute.allowed.has(clock.minute)) {
            return new Date(cursor)
        }
        cursor += MINUTE_MS
    }
    return null
}

function parseField(raw: string, spec: (typeof FIELDS)[number]): CronField {
    const allowed = new Set<number>()
    let wildcard = false
    for (const term of raw.split(',')) {
        if (term.trim() === '') {
            throw new CronError(`Empty ${spec.name} field.`)
        }
        wildcard = parseTerm(term.trim(), spec, allowed) || wildcard
    }
    if (allowed.size === 0) {
        throw new CronError(`The ${spec.name} field matches nothing.`)
    }
    return { allowed, wildcard }
}

/** Adds one comma-separated term to `allowed`; returns whether the term covers the whole field. */
function parseTerm(term: string, spec: (typeof FIELDS)[number], allowed: Set<number>): boolean {
    const [range, stepRaw, ...rest] = term.split('/')
    if (rest.length > 0 || stepRaw === '') {
        throw new CronError(`Could not read "${term}" in the ${spec.name} field.`)
    }
    const step = stepRaw === undefined ? 1 : Number(stepRaw)
    if (!Number.isInteger(step) || step < 1) {
        throw new CronError(`The step in "${term}" (${spec.name}) must be a whole number of 1 or more.`)
    }
    const { from, to, whole } = bounds(range ?? '*', spec, term)
    for (let value = from; value <= to; value += step) {
        // Cron's Sunday is 0 and 7; folding here keeps every later comparison on 0-6.
        allowed.add(spec.name === 'day of week' && value === 7 ? 0 : value)
    }
    return whole && step === 1
}

function bounds(
    range: string,
    spec: (typeof FIELDS)[number],
    term: string,
): { from: number; to: number; whole: boolean } {
    if (range === '*') {
        return { from: spec.min, to: spec.max, whole: true }
    }
    const [startRaw, endRaw, ...rest] = range.split('-')
    if (rest.length > 0) {
        throw new CronError(`Could not read "${term}" in the ${spec.name} field.`)
    }
    const from = numberIn(startRaw ?? '', spec, term)
    let to = from
    if (endRaw !== undefined) {
        to = numberIn(endRaw, spec, term)
    } else if (term.includes('/')) {
        // A bare step counts from its start to the end of the field, the way cron reads `5/10`.
        to = spec.max
    }
    if (to < from) {
        throw new CronError(`"${term}" runs backwards in the ${spec.name} field.`)
    }
    return { from, to, whole: from === spec.min && to === spec.max }
}

function numberIn(raw: string, spec: (typeof FIELDS)[number], term: string): number {
    const named = spec.names.indexOf(raw.toLowerCase())
    const value = named === -1 ? Number(raw) : named + spec.min
    if (raw.trim() === '' || !Number.isInteger(value) || value < spec.min || value > spec.max) {
        throw new CronError(`"${term}" is out of range in the ${spec.name} field (${spec.min}-${spec.max}).`)
    }
    return value
}
